import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import supabase from '../services/supabase'
import SchemaMarkup from './SchemaMarkup'

const Breadcrumbs = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location
  const params = useParams()
  const [detailTitle, setDetailTitle] = useState('')

  // Function to go back to the previous page
  const goBack = () => {
    navigate(-1)
  }

  // Fetch detail title for detail pages
  useEffect(() => {
    const fetchDetailTitle = async () => {
      try {
        // IAE detail page
        if (pathname.includes('/iae/') && params.cod_iae && params.slug_iae) {
          const { data, error } = await supabase.rpc(
            'get_iae_details_by_slug',
            {
              input_slug: params.slug_iae,
            }
          )

          if (!error && data && data.length > 0) {
            setDetailTitle(data[0].titulo_iae)
          }
        }
        // CNAE 2025 detail page
        else if (
          pathname.includes('/cnae-2025/') &&
          params.cod_cnae &&
          params.slug_cnae
        ) {
          const { data, error } = await supabase.rpc(
            'get_cnae_2025_details_by_slug',
            {
              input_slug: params.slug_cnae,
            }
          )

          if (!error && data && data.length > 0) {
            setDetailTitle(data[0].titulo_cnae)
          }
        }
        // CNAE 2009 detail page
        else if (
          pathname.includes('/cnae-2009/') &&
          params.cod_cnae_2009 &&
          params.slug_cnae_2009
        ) {
          const { data, error } = await supabase.rpc(
            'get_cnae_2009_details_by_slug',
            {
              input_slug: params.slug_cnae_2009,
            }
          )

          if (!error && data && data.length > 0) {
            setDetailTitle(data[0].titulo_cnae_2009)
          }
        }
      } catch (error) {
        console.error('Error fetching detail title:', error)
      }
    }

    if (
      pathname.includes('/iae/') ||
      pathname.includes('/cnae-2025/') ||
      pathname.includes('/cnae-2009/')
    ) {
      fetchDetailTitle()
    }
  }, [pathname, params])

  // Function to generate breadcrumbs based on the current path
  const generateBreadcrumbs = () => {
    // Skip the first empty string after splitting
    const pathnames = pathname.split('/').filter((x) => x)

    // Base URL for schema
    const baseUrl = 'https://conversoriaecnae.es'

    // Initialize breadcrumbs with Home
    let breadcrumbs = [
      {
        name: 'Inicio',
        path: '/',
        url: baseUrl,
      },
    ]

    // Build breadcrumb items based on the path
    let currentPath = ''

    // Handle special cases for detail pages
    if (pathname.includes('/iae/') && params.cod_iae && params.slug_iae) {
      // IAE detail page
      breadcrumbs.push({
        name: 'Epígrafes IAE',
        path: '/epigrafes-iae/',
        url: `${baseUrl}/epigrafes-iae/`,
      })

      breadcrumbs.push({
        name: params.cod_iae,
        path: null, // No link for the code
        url: `${baseUrl}${pathname}`,
      })

      return breadcrumbs
    } else if (
      pathname.includes('/cnae-2025/') &&
      params.cod_cnae &&
      params.slug_cnae
    ) {
      // CNAE 2025 detail page
      breadcrumbs.push({
        name: 'CNAE 2025',
        path: '/codigos-cnae-2025/',
        url: `${baseUrl}/codigos-cnae-2025/`,
      })

      breadcrumbs.push({
        name: params.cod_cnae,
        path: null, // No link for the code
        url: `${baseUrl}${pathname}`,
      })

      return breadcrumbs
    } else if (
      pathname.includes('/cnae-2009/') &&
      params.cod_cnae_2009 &&
      params.slug_cnae_2009
    ) {
      // CNAE 2009 detail page
      breadcrumbs.push({
        name: 'Códigos CNAE 2009',
        path: '/codigos-cnae-2009/',
        url: `${baseUrl}/codigos-cnae-2009/`,
      })

      breadcrumbs.push({
        name: params.cod_cnae_2009,
        path: null, // No link for the code
        url: `${baseUrl}${pathname}`,
      })

      return breadcrumbs
    }

    // Standard breadcrumb generation for other pages
    pathnames.forEach((name, index) => {
      currentPath += `/${name}`

      // Skip URL parameters (those with : prefix in routes)
      if (name.includes(':')) return

      // Format the name for display
      let displayName = name
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase())

      // Special cases for specific routes
      switch (name) {
        case 'epigrafes-iae':
          displayName = 'Epígrafes IAE'
          break
        case 'codigos-cnae-2025':
          displayName = 'Códigos CNAE 2025'
          break
        case 'codigos-cnae-2009':
          displayName = 'Códigos CNAE 2009'
          break
        case 'correspondencia-entre-cnae-2025-cnae-2009':
          displayName = 'Correspondencias CNAE'
          break
        case 'newsletter':
          displayName = 'Newsletter'
          break
        case 'cookies-configuracion':
          displayName = 'Configuración de Cookies'
          break
        case 'terminos-condiciones':
          displayName = 'Términos y Condiciones'
          break
        case 'politica-privacidad':
          displayName = 'Política de Privacidad'
          break
      }

      breadcrumbs.push({
        name: displayName,
        path: currentPath,
        url: `${baseUrl}${currentPath}`,
      })
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  // Don't show breadcrumbs on the home page
  if (pathname === '/') {
    return null
  }

  return (
    <div className='bg-surface border-b border-border py-3'>
      <div className='container mx-auto px-4'>
        <div className='flex items-center text-sm'>
          {/* Back button */}
          <button
            onClick={goBack}
            className='mr-3 text-text-primary hover:text-primary transition-colors'
            aria-label='Volver atrás'
          >
            <FaArrowLeft />
          </button>

          {/* Breadcrumbs */}
          <nav className='flex items-center'>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.path}>
                {index > 0 && (
                  <span className='mx-2 text-text-secondary'>/</span>
                )}
                {index === breadcrumbs.length - 1 ||
                breadcrumb.path === null ? (
                  <span className='text-text-secondary'>
                    {breadcrumb.name}
                    {detailTitle &&
                      pathname.includes('/iae/') &&
                      index === breadcrumbs.length - 1 && (
                        <span className='ml-2 text-text-secondary'>
                          / {detailTitle}
                        </span>
                      )}
                    {detailTitle &&
                      pathname.includes('/cnae-2025/') &&
                      index === breadcrumbs.length - 1 && (
                        <span className='ml-2 text-text-secondary'>
                          / {detailTitle}
                        </span>
                      )}
                    {detailTitle &&
                      pathname.includes('/cnae-2009/') &&
                      index === breadcrumbs.length - 1 && (
                        <span className='ml-2 text-text-secondary'>
                          / {detailTitle}
                        </span>
                      )}
                  </span>
                ) : (
                  <Link
                    to={breadcrumb.path}
                    className='text-text-primary hover:text-primary transition-colors'
                  >
                    {breadcrumb.name}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* BreadcrumbList Schema */}
          <SchemaMarkup
            type='BreadcrumbList'
            data={{
              itemListElement: breadcrumbs.map((breadcrumb, index) => ({
                position: index + 1,
                name: breadcrumb.name,
                item: breadcrumb.url,
              })),
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default Breadcrumbs
